'use client'

import React, { useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useQuery } from '@tanstack/react-query'
import { Users, AlertCircle, TrendingUp, Activity, Wifi, WifiOff } from 'lucide-react'

interface RealtimeDashboardCompactProps {
  wardId?: string
}

export function RealtimeDashboardCompact({ wardId = '3A' }: RealtimeDashboardCompactProps) {
  const [isConnected, setIsConnected] = useState(true)

  // 전체 환자 데이터 가져오기 (통계용)
  const { data: allPatientsData, error } = useQuery({
    queryKey: ['all-patients-realtime'],
    queryFn: async () => {
      // 모든 병동 데이터 가져오기
      const allWards = ['3A', '3B', '4A', '4B', '5A', '5B', '6A', '6B', '7A', '7B', '8A', '8B', '9ICU', '10VIP']
      const allPredictions = []

      for (const ward of allWards) {
        try {
          const response = await fetch(`/api/v1/inference/risk?ward_id=${ward}`)
          if (response.ok) {
            const data = await response.json()
            if (data.predictions && data.predictions.length > 0) {
              allPredictions.push(...data.predictions)
            }
          }
        } catch (error) {
          console.error(`Failed to fetch data for ward ${ward}:`, error)
        }
      }

      setIsConnected(true)
      return {
        predictions: allPredictions,
        totalPatients: allPredictions.length,
        pagination: {
          total: allPredictions.length
        }
      }
    },
    refetchInterval: 30000 // 30초마다 업데이트
  })

  const allPatients = allPatientsData?.predictions || []
  const totalPatients = allPatients.length || 200

  // 메트릭 계산 - 전체 병원 통계
  const metrics = {
    totalPatients: totalPatients,
    activeAlerts: allPatients.filter((p: any) => p.riskScore >= 0.75).length,
    avgRiskScore: allPatients.length > 0
      ? (allPatients.reduce((sum: number, p: any) => sum + p.riskScore, 0) / allPatients.length * 100).toFixed(1)
      : '0.0',
    occupancy: `${totalPatients}/200 (전체 병상)`
  }

  return (
    <Card className="mb-4">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-sm">실시간 모니터링</h3>
            {isConnected ? (
              <Badge variant="outline" className="text-xs h-5 bg-green-50">
                <Wifi className="h-3 w-3 mr-1" />
                연결
              </Badge>
            ) : (
              <Badge variant="destructive" className="text-xs h-5">
                <WifiOff className="h-3 w-3 mr-1" />
                끊김
              </Badge>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div className="bg-muted/50 rounded-lg p-2.5">
            <div className="flex items-center gap-1 text-xs text-muted-foreground mb-0.5">
              <Users className="h-3 w-3" />
              환자
            </div>
            <div className="text-lg font-semibold">{metrics.totalPatients}</div>
          </div>

          <div className={`rounded-lg p-2.5 ${metrics.activeAlerts > 0 ? 'bg-red-50' : 'bg-muted/50'}`}>
            <div className={`flex items-center gap-1 text-xs mb-0.5 ${
              metrics.activeAlerts > 0 ? 'text-red-600' : 'text-muted-foreground'
            }`}>
              <AlertCircle className="h-3 w-3" />
              경보
            </div>
            <div className={`text-lg font-semibold ${
              metrics.activeAlerts > 0 ? 'text-red-600' : ''
            }`}>{metrics.activeAlerts}</div>
          </div>

          <div className="bg-muted/50 rounded-lg p-2.5">
            <div className="flex items-center gap-1 text-xs text-muted-foreground mb-0.5">
              <TrendingUp className="h-3 w-3" />
              위험도
            </div>
            <div className="text-lg font-semibold">{metrics.avgRiskScore}%</div>
          </div>

          <div className="bg-muted/50 rounded-lg p-2.5">
            <div className="flex items-center gap-1 text-xs text-muted-foreground mb-0.5">
              <Activity className="h-3 w-3" />
              병상
            </div>
            <div className="text-lg font-semibold">{metrics.occupancy}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}